import * as React from "react";
import type { Session } from "@supabase/supabase-js";
import {
  DEV_AUTH_BYPASS,
  DEV_USER,
  getSupabase,
  isAllowedEmail,
  signOut as authSignOut,
  userFromSession,
  type AuthUser,
} from "./auth";

interface AuthCtx {
  user: AuthUser | null;
  session: Session | null;
  loading: boolean;
  /** True if the user is signed in but their email isn't allowlisted. */
  forbidden: boolean;
  signOut: () => Promise<void>;
  refresh: () => Promise<void>;
}

const Ctx = React.createContext<AuthCtx | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = React.useState<Session | null>(null);
  const [user, setUser] = React.useState<AuthUser | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [forbidden, setForbidden] = React.useState(false);

  const handle = React.useCallback((s: Session | null) => {
    setSession(s);
    const u = userFromSession(s);
    if (u && !isAllowedEmail(u.email)) {
      setForbidden(true);
      setUser(null);
    } else {
      setForbidden(false);
      setUser(u);
    }
    setLoading(false);
  }, []);

  React.useEffect(() => {
    if (DEV_AUTH_BYPASS) {
      setUser(DEV_USER);
      setSession(null);
      setLoading(false);
      return;
    }
    const supabase = getSupabase();
    if (!supabase) {
      setLoading(false);
      return;
    }
    let unsub = () => {};
    (async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      handle(session);
      const { data: sub } = supabase.auth.onAuthStateChange((_e, s) => handle(s));
      unsub = () => sub.subscription.unsubscribe();
    })();
    return () => unsub();
  }, [handle]);

  const refresh = React.useCallback(async () => {
    const supabase = getSupabase();
    if (!supabase) return;
    const {
      data: { session },
    } = await supabase.auth.getSession();
    handle(session);
  }, [handle]);

  const value: AuthCtx = {
    user,
    session,
    loading,
    forbidden,
    signOut: async () => {
      await authSignOut();
      setSession(null);
      setUser(null);
    },
    refresh,
  };

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthCtx {
  const v = React.useContext(Ctx);
  if (!v) throw new Error("useAuth must be used within AuthProvider");
  return v;
}
